import React, { useEffect, useState, useRef } from 'react';
/**
 * CategorizeRenderer — Assign items to categories by tapping.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */

import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
export function CategorizeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const categories: string[] = config.categories || [];
  const items: Array<{
    text: string;
    correctCategory: number;
  }> = config.items || [];
  const [assignments, setAssignments] = useState<Record<number, number>>({});
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setAssignments({});
    setSelectedItem(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(Object.keys(assignments).length === items.length && items.length > 0);
  }, [assignments, items.length, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(assignments));
      setSelectedItem(null);
    }
  }, [shouldValidate, assignments, onAnswer]);
  const handleItemClick = (index: number) => {
    if (shouldValidate) return;
    if (assignments[index] !== undefined) {
      const next = {
        ...assignments
      };
      delete next[index];
      setAssignments(next);
      setSelectedItem(index);
    } else {
      setSelectedItem(selectedItem === index ? null : index);
    }
  };
  const handleCategoryClick = (categoryIndex: number) => {
    if (shouldValidate || selectedItem === null) return;
    setAssignments(prev => ({
      ...prev,
      [selectedItem]: categoryIndex
    }));
    setSelectedItem(null);
  };
  if (categories.length === 0 || items.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-6995">
        <p className="text-red-700 font-bold" data-id="element-6996">
          ⚠️ Configuratie ontbreekt (categories/items)
        </p>
      </div>;
  }
  const unassignedItems = items.map((item, index) => ({
    ...item,
    index
  })).filter(item => assignments[item.index] === undefined);
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-8" data-id="element-6997">
      {/* Unassigned Items Pool */}
      <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200 shadow-inner min-h-[120px]" data-id="element-6998">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 text-center" data-id="element-6999">
          {unassignedItems.length > 0 ? 'Kies een woord en tik op een vak' : 'Alles is gesorteerd!'}
        </h3>
        <div className="flex flex-wrap justify-center gap-3" data-id="element-7000">
          <AnimatePresence mode="popLayout" data-id="element-7001">
            {unassignedItems.map(item => {
            const isSelected = selectedItem === item.index;
            return <motion.button layout layoutId={`item-${item.index}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} key={`item-${item.index}`} onClick={() => handleItemClick(item.index)} disabled={shouldValidate} className={`px-5 py-3 rounded-xl font-bold text-lg transition-all shadow-sm ${shouldValidate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1 hover:shadow-md'} ${isSelected ? 'bg-indigo-500 text-white shadow-indigo-200 ring-4 ring-indigo-200 scale-105' : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-indigo-300'}`} aria-pressed={isSelected} data-id="element-7002">
                  {item.text}
                </motion.button>;
          })}
          </AnimatePresence>
          {unassignedItems.length === 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex items-center justify-center text-emerald-500 font-bold gap-2 py-2" data-id="element-7003">
              <CheckCircle2 className="w-6 h-6" data-id="element-7004" />
              <span data-id="element-7005">Klaar om te controleren</span>
            </motion.div>}
        </div>
      </div>

      {/* Categories Grid */}
      <div className={`grid gap-4 ${categories.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`} data-id="element-7006">
        {categories.map((category, catIndex) => {
        const categoryItems = items.map((item, index) => ({
          ...item,
          index
        })).filter(item => assignments[item.index] === catIndex);
        const isTargetable = selectedItem !== null && !shouldValidate;
        return <div key={`cat-${catIndex}`} onClick={() => handleCategoryClick(catIndex)} className={`flex flex-col rounded-2xl border-2 transition-all overflow-hidden min-h-[200px] ${isTargetable ? 'cursor-pointer ring-4 ring-indigo-100 border-indigo-300 bg-indigo-50/30' : 'border-slate-200 bg-white'} ${shouldValidate ? 'opacity-90' : ''}`} role="region" aria-label={`Categorie: ${category}`} data-id="element-7007">
              <div className={`p-4 text-center border-b-2 font-bold text-lg ${isTargetable ? 'bg-indigo-100 border-indigo-200 text-indigo-800' : 'bg-slate-100 border-slate-200 text-slate-700'}`} data-id="element-7008">
                {category}
                <div className="text-xs font-medium opacity-60 mt-1" data-id="element-7009">
                  {categoryItems.length}{' '}
                  {categoryItems.length === 1 ? 'woord' : 'woorden'}
                </div>
              </div>
              <div className={`flex-1 p-4 flex flex-col gap-2 ${isTargetable ? 'bg-indigo-50/50' : ''}`} data-id="element-7010">
                <AnimatePresence mode="popLayout" data-id="element-7011">
                  {categoryItems.map(item => {
                let validationClass = 'bg-white border-slate-200 text-slate-700';
                let ValidationIcon = null;
                if (shouldValidate) {
                  const isCorrect = item.correctCategory === catIndex;
                  validationClass = isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800';
                  ValidationIcon = isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-7012" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" data-id="element-7013" />;
                }
                return <motion.button layout layoutId={`item-${item.index}`} initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} key={`item-${item.index}`} onClick={e => {
                  e.stopPropagation();
                  handleItemClick(item.index);
                }} disabled={shouldValidate} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium border-2 shadow-sm ${shouldValidate ? 'cursor-default' : 'cursor-pointer hover:border-slate-300 hover:bg-slate-50 hover:shadow'} ${validationClass}`} data-id="element-7014">
                        <span className="truncate" data-id="element-7015">{item.text}</span>
                        {ValidationIcon}
                      </motion.button>;
              })}
                </AnimatePresence>
                {categoryItems.length === 0 && <div className="flex-1 flex items-center justify-center text-slate-400 text-sm font-medium border-2 border-dashed border-slate-200 rounded-xl m-2 bg-slate-50/50" data-id="element-7016">
                    {isTargetable ? 'Tik hier om te plaatsen' : 'Leeg'}
                  </div>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}